import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';

const Location = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (auth.currentUser?.uid) fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const q = query(
        collection(db, 'locations'),
        where('propid', '==', auth.currentUser?.uid)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const handleOpen = () => {
    setFormData({ name: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { name } = formData;

    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    setLoading(true);

    try {
      const propid = auth.currentUser?.uid;
      if (!propid) throw new Error("User not authenticated");

      if (editMode && editId) {
        await updateDoc(doc(db, 'locations', editId), { name, propid });
        toast.success("Location updated");
      } else {
        await addDoc(collection(db, 'locations'), { name, propid });
        toast.success("Location added");
      }

      fetchLocations();
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (locations) => {
    setFormData({ name: locations.name });
    setEditId(Location.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'locations', id));
      toast.success("Location deleted");
      fetchLocations();
    } catch (err) {
      console.error(err);
      toast.error("Error deleting Location");
    }
  };

  const paginated = locations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Location</Button>
        <Typography variant="h5" fontWeight="bold">Locations</Typography>
      </Stack>

      <Card>
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">No locations found</TableCell>
                </TableRow>
              ) : (
                paginated.map((Location, index) => (
                  <TableRow key={Location.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(Location)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(Location.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{Location.name}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={locations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Location</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Location;
